/**
 * Schedule KPI Configuration Wizard (DEV-840).
 *
 * Mirrors MissionKpiWizard for Schedule progress sub-KPIs. 4-step stepped
 * wizard for Add/Edit:
 *
 *   Step 1: Display name + Template variant (Progress Only / Progress + SPI)
 *   Step 2: Tolerances — variance thresholds (always) and SPI thresholds
 *           (when variant is progress_spi)
 *   Step 3: Reporting cadence + role assignment + permissible_days_late
 *   Step 4: Review & save
 *
 * Add mode: Linear flow (Next/Back, steps not clickable).
 * Edit mode: All steps are jump points; "Show all" toggle flattens to a
 * single page (mirrors Mission edit mode).
 *
 * REUSE: visual treatment is intentionally identical to MissionKpiWizard —
 * we import its CSS module directly so future style changes touch one file.
 * If the two wizards visually diverge later, extract shared styles to a
 * sibling kpiWizardCommon.module.css; for now, single-source is correct.
 *
 * SCOPE: Schedule has only ONE KPI shape (% complete over time), with two
 * sub-template variants (Progress Only, Progress + SPI). There is no
 * plug-in registry like Mission has — adding one would build extensibility
 * infrastructure with no second consumer. See DEV-840 ticket "out of scope."
 */

import React, { useReducer, useMemo } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { SubKpiInstance } from '../../Data/ProjectConfig/projectKpiConfigOverrides';
import {
  ReportingCadenceWidget,
  DEFAULT_CADENCE_VALUE,
  type CadenceValue,
} from '../ReportingCadenceWidget/ReportingCadenceWidget';
// Reuse Mission wizard's CSS module — same visual treatment by design.
import styles from './MissionKpiWizard.module.css';

/* ── Types ─────────────────────────────────────────────────── */

type WizardMode = 'add' | 'edit';
export type ScheduleTemplateType = 'progress' | 'progress_spi';

export interface ScheduleKpiWizardProps {
  mode: WizardMode;
  existingInstance?: SubKpiInstance;
  /** Project guid passed for parity with MissionKpiWizard; not used today
   *  but reserved for future role-list scoping per project. */
  projectGuid: string;
  onSave: (instance: SubKpiInstance) => void;
  onCancel: () => void;
}

interface WizardState {
  displayName: string;
  /**
   * Full = renders the full chart on Performance tab; Mini = compact summary.
   * Preserved from legacy form so existing rows round-trip cleanly.
   */
  displayMode: 'full' | 'mini';
  templateType: ScheduleTemplateType;
  // Progress variance thresholds (always applicable).
  varianceYellowPP: number;
  varianceRedPP: number;
  // SPI thresholds (only meaningful when templateType === 'progress_spi').
  spiWarnThreshold: number;
  spiCritThreshold: number;
  spiMinBaselinePct: number;
  spiWeightPct: number; // SPI weight as % of total; progress = 100 - this.
  cadenceValue: CadenceValue;
  assignedRoleGuid: string | null;
  permissibleDaysLate: number;
  currentStep: number;
  showAll: boolean;
  errors: Record<string, string>;
}

type WizardAction =
  | { type: 'SET_DISPLAY_NAME'; value: string }
  | { type: 'SET_DISPLAY_MODE'; value: 'full' | 'mini' }
  | { type: 'SET_TEMPLATE_TYPE'; value: ScheduleTemplateType }
  | { type: 'SET_VARIANCE_YELLOW'; value: number }
  | { type: 'SET_VARIANCE_RED'; value: number }
  | { type: 'SET_SPI_WARN'; value: number }
  | { type: 'SET_SPI_CRIT'; value: number }
  | { type: 'SET_SPI_MIN_BASELINE'; value: number }
  | { type: 'SET_SPI_WEIGHT'; value: number }
  | { type: 'SET_CADENCE'; value: CadenceValue }
  | { type: 'SET_ASSIGNED_ROLE'; value: string | null }
  | { type: 'SET_PERMISSIBLE_DAYS_LATE'; value: number }
  | { type: 'SET_STEP'; value: number }
  | { type: 'TOGGLE_SHOW_ALL' }
  | { type: 'SET_ERRORS'; value: Record<string, string> }
  | { type: 'CLEAR_ERRORS' };

/* ── Initial State & Reducer ───────────────────────────────── */

function createInitialState(mode: WizardMode, existing?: SubKpiInstance): WizardState {
  if (mode === 'edit' && existing) {
    const inst = existing as any;
    const rawTmpl = String(inst?.schedule_template_type || '');
    const templateType: ScheduleTemplateType =
      rawTmpl === 'progress_spi' ? 'progress_spi' : 'progress';
    return {
      displayName: existing.display_name || '',
      displayMode: (existing.display_mode === 'mini' ? 'mini' : 'full') as 'full' | 'mini',
      templateType,
      varianceYellowPP: Number.isFinite(inst.variance_yellow_pp) ? inst.variance_yellow_pp : 5,
      varianceRedPP: Number.isFinite(inst.variance_red_pp) ? inst.variance_red_pp : 10,
      spiWarnThreshold: Number.isFinite(inst.spi_warning_threshold) ? inst.spi_warning_threshold : 0.95,
      spiCritThreshold: Number.isFinite(inst.spi_critical_threshold) ? inst.spi_critical_threshold : 0.9,
      spiMinBaselinePct: Number.isFinite(inst.spi_min_baseline_pct) ? inst.spi_min_baseline_pct : 5,
      spiWeightPct: Number.isFinite(inst.spi_weight_pct) ? inst.spi_weight_pct : 50,
      cadenceValue: {
        ...DEFAULT_CADENCE_VALUE,
        cadence: (existing.cadence as any) || 'weekly',
      },
      assignedRoleGuid: (inst.assigned_role_guid as string | undefined) ?? null,
      permissibleDaysLate:
        typeof inst.permissible_days_late === 'number' ? inst.permissible_days_late : 2,
      currentStep: 2, // Land on Tolerances step in edit mode (most common edit target).
      showAll: false,
      errors: {},
    };
  }

  // Add mode — fresh defaults that match the legacy form's initial state
  // so users get the same starting point as before.
  return {
    displayName: '',
    displayMode: 'full' as 'full' | 'mini',
    templateType: 'progress',
    varianceYellowPP: 5,
    varianceRedPP: 10,
    spiWarnThreshold: 0.95,
    spiCritThreshold: 0.9,
    spiMinBaselinePct: 5,
    spiWeightPct: 50,
    cadenceValue: DEFAULT_CADENCE_VALUE,
    assignedRoleGuid: null,
    permissibleDaysLate: 2,
    currentStep: 1,
    showAll: false,
    errors: {},
  };
}

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_DISPLAY_NAME':       return { ...state, displayName: action.value };
    case 'SET_DISPLAY_MODE':       return { ...state, displayMode: action.value };
    case 'SET_TEMPLATE_TYPE':      return { ...state, templateType: action.value };
    case 'SET_VARIANCE_YELLOW':    return { ...state, varianceYellowPP: action.value };
    case 'SET_VARIANCE_RED':       return { ...state, varianceRedPP: action.value };
    case 'SET_SPI_WARN':           return { ...state, spiWarnThreshold: action.value };
    case 'SET_SPI_CRIT':           return { ...state, spiCritThreshold: action.value };
    case 'SET_SPI_MIN_BASELINE':   return { ...state, spiMinBaselinePct: action.value };
    case 'SET_SPI_WEIGHT':         return { ...state, spiWeightPct: action.value };
    case 'SET_CADENCE':            return { ...state, cadenceValue: action.value };
    case 'SET_ASSIGNED_ROLE':      return { ...state, assignedRoleGuid: action.value };
    case 'SET_PERMISSIBLE_DAYS_LATE': return { ...state, permissibleDaysLate: action.value };
    case 'SET_STEP':               return { ...state, currentStep: action.value };
    case 'TOGGLE_SHOW_ALL':        return { ...state, showAll: !state.showAll };
    case 'SET_ERRORS':             return { ...state, errors: action.value };
    case 'CLEAR_ERRORS':           return { ...state, errors: {} };
    default:                       return state;
  }
}

/* ── Validation ────────────────────────────────────────────── */

function validateStep(step: number, state: WizardState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (step === 1) {
    if (!state.displayName.trim()) {
      errors.displayName = 'Display name is required.';
    }
  }
  if (step === 2) {
    if (!Number.isFinite(state.varianceYellowPP) || state.varianceYellowPP < 0) {
      errors.varianceYellowPP = 'Variance Yellow must be a non-negative number.';
    }
    if (!Number.isFinite(state.varianceRedPP) || state.varianceRedPP < 0) {
      errors.varianceRedPP = 'Variance Red must be a non-negative number.';
    }
    if (
      Number.isFinite(state.varianceYellowPP) &&
      Number.isFinite(state.varianceRedPP) &&
      state.varianceYellowPP >= state.varianceRedPP
    ) {
      errors.varianceRedPP = 'Variance Red must be greater than Variance Yellow (Red is the worse band).';
    }
    if (state.templateType === 'progress_spi') {
      if (!Number.isFinite(state.spiWarnThreshold) || state.spiWarnThreshold <= 0 || state.spiWarnThreshold > 2) {
        errors.spiWarn = 'SPI Warning must be between 0 and 2.';
      }
      if (!Number.isFinite(state.spiCritThreshold) || state.spiCritThreshold <= 0 || state.spiCritThreshold > 2) {
        errors.spiCrit = 'SPI Critical must be between 0 and 2.';
      }
      if (
        Number.isFinite(state.spiWarnThreshold) &&
        Number.isFinite(state.spiCritThreshold) &&
        state.spiCritThreshold >= state.spiWarnThreshold
      ) {
        errors.spiCrit = 'SPI Critical must be lower than SPI Warning (Critical is the worse band).';
      }
    }
  }
  if (step === 3) {
    if (!Number.isFinite(state.permissibleDaysLate) || state.permissibleDaysLate < 0) {
      errors.permissibleDaysLate = 'Permissible days late must be a non-negative number.';
    }
  }
  return errors;
}

/* ── Component ─────────────────────────────────────────────── */

const STEP_TITLES = [
  'Name & Template',
  'Tolerances',
  'Reporting',
  'Review',
];

export function ScheduleKpiWizard({
  mode,
  existingInstance,
  onSave,
  onCancel,
}: ScheduleKpiWizardProps) {
  const { t } = useAppTranslation('projectConfig');
  const [state, dispatch] = useReducer(wizardReducer, mode, (m) =>
    createInitialState(m, existingInstance),
  );

  const lastStep = 4;
  const firstStep = 1;

  const handleStepClick = (stepNum: number) => {
    if (mode === 'edit') dispatch({ type: 'SET_STEP', value: stepNum });
  };

  const handleNext = () => {
    if (state.currentStep < lastStep) {
      const stepErrors = validateStep(state.currentStep, state);
      if (Object.keys(stepErrors).length > 0) {
        dispatch({ type: 'SET_ERRORS', value: stepErrors });
        return;
      }
      dispatch({ type: 'CLEAR_ERRORS' });
      dispatch({ type: 'SET_STEP', value: state.currentStep + 1 });
    } else {
      handleSave();
    }
  };

  const handleBack = () => {
    if (state.currentStep > firstStep) {
      dispatch({ type: 'CLEAR_ERRORS' });
      dispatch({ type: 'SET_STEP', value: state.currentStep - 1 });
    }
  };

  const handleSave = () => {
    // Validate all steps. In edit mode (showAll), also validate steps the
    // user hasn't visited so they don't slip past required fields.
    const allErrors: Record<string, string> = {};
    for (let step = 1; step <= lastStep; step++) {
      Object.assign(allErrors, validateStep(step, state));
    }
    if (Object.keys(allErrors).length > 0) {
      dispatch({ type: 'SET_ERRORS', value: allErrors });
      // Jump to the first step that has an error so the user sees it.
      for (let step = 1; step <= lastStep; step++) {
        const stepErrs = validateStep(step, state);
        if (Object.keys(stepErrs).length > 0) {
          dispatch({ type: 'SET_STEP', value: step });
          break;
        }
      }
      return;
    }

    // Build the SubKpiInstance. Schedule progress KPIs save into the legacy
    // engine fields directly — no separate "schedule_type" plug-in registry
    // exists (and shouldn't, per DEV-840 out-of-scope note).
    const instance: SubKpiInstance = {
      ...(existingInstance || {}),
      instance_guid: existingInstance?.instance_guid || generateGuid(),
      catalog_kpi_guid:
        (existingInstance as any)?.catalog_kpi_guid || 'kpi_sched_progress_v1',
      display_name: state.displayName || 'Schedule Progress',
      // DEV-840 follow-up: display_mode comes from the wizard now (Full / Mini),
      // not silently inherited from existingInstance. User-controlled per legacy form.
      display_mode: state.displayMode,
      is_mandatory: existingInstance?.is_mandatory ?? false,
      direction: 'increase_is_good' as any, // Schedule is always "more progress = better"
      cadence: state.cadenceValue.cadence as any,
      assigned_role_guid: state.assignedRoleGuid || undefined,
      permissible_days_late: Number.isFinite(state.permissibleDaysLate)
        ? state.permissibleDaysLate
        : undefined,
    } as any;

    // Schedule-specific fields — same field names the legacy form wrote, so
    // existing rows round-trip cleanly through the wizard.
    (instance as any).schedule_template_type = state.templateType;
    (instance as any).variance_yellow_pp = Number(state.varianceYellowPP);
    (instance as any).variance_red_pp = Number(state.varianceRedPP);
    if (state.templateType === 'progress_spi') {
      (instance as any).spi_warning_threshold = Number(state.spiWarnThreshold);
      (instance as any).spi_critical_threshold = Number(state.spiCritThreshold);
      (instance as any).spi_min_baseline_pct = Number(state.spiMinBaselinePct);
      (instance as any).spi_weight_pct = Number(state.spiWeightPct);
    }

    if (typeof console !== 'undefined') {
      console.debug('[ScheduleKpiWizard] saving', {
        display_name: instance.display_name,
        template: state.templateType,
        variance: { yellow: state.varianceYellowPP, red: state.varianceRedPP },
        spi:
          state.templateType === 'progress_spi'
            ? {
                warn: state.spiWarnThreshold,
                crit: state.spiCritThreshold,
                minBaseline: state.spiMinBaselinePct,
                weight: state.spiWeightPct,
              }
            : null,
        cadence: state.cadenceValue.cadence,
        role: state.assignedRoleGuid,
        daysLate: state.permissibleDaysLate,
      });
    }

    onSave(instance);
  };

  const showStep = (n: number) => state.showAll || state.currentStep === n;

  return (
    <div className={styles.wizardContainer}>
      <div className={styles.wizardHeader}>
        <h2 className={styles.wizardTitle}>
          {mode === 'edit'
            ? t('kpi.editScheduleKpi', 'Edit Schedule KPI')
            : t('kpi.addScheduleKpi', 'Add Schedule KPI')}
        </h2>
        <button className={styles.closeBtn} onClick={onCancel} aria-label="Close">
          ×
        </button>
      </div>

      {mode === 'edit' && (
        <div className={styles.editBanner}>
          <span>{t('kpi.editingExisting', 'Editing existing KPI — jump to any step.')}</span>
          <label className={styles.showAllToggle}>
            <input
              type="checkbox"
              checked={state.showAll}
              onChange={() => dispatch({ type: 'TOGGLE_SHOW_ALL' })}
            />
            {t('kpi.showAllSections', 'Show all sections')}
          </label>
        </div>
      )}

      {/* Stepper — mirrors MissionKpiWizard's class structure */}
      <div className={styles.stepper}>
        <div className={styles.steps}>
          {STEP_TITLES.map((title, idx) => {
            const stepNum = idx + 1;
            const isActive = state.currentStep === stepNum;
            const isLinearFlow = mode === 'add';
            const stateClass = isLinearFlow
              ? stepNum < state.currentStep
                ? styles.done
                : isActive
                ? styles.active
                : ''
              : state.showAll
              ? styles.done
              : isActive
              ? styles.active
              : styles.done;
            return (
              <React.Fragment key={stepNum}>
                <div
                  className={`${styles.step} ${stateClass} ${
                    mode === 'edit' && !state.showAll ? styles.clickable : ''
                  }`}
                  onClick={() => handleStepClick(stepNum)}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <div className={styles.stepNum}>{stepNum}</div>
                  <div className={styles.stepLabel}>{title}</div>
                </div>
                {idx < STEP_TITLES.length - 1 && <div className={styles.stepConn} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Body — scrollable container */}
      <div className={styles.modalBody}>
        {/* Step 1: Name + Template */}
        {showStep(1) && (
          <div className={styles.stepContent} data-step="1">
            <h3 className={styles.stepTitle}>{t('kpi.scheduleStep1Title', 'Name and template')}</h3>
            <p className={styles.stepDesc}>
              {t(
                'kpi.scheduleStep1Desc',
                'Give this Schedule KPI a recognizable name and pick the template variant.',
              )}
            </p>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                {t('kpi.displayName', 'Display name')}
                <span className={styles.required}> *</span>
              </label>
              <input
                type="text"
                className={styles.input}
                value={state.displayName}
                onChange={(e) => dispatch({ type: 'SET_DISPLAY_NAME', value: e.target.value })}
                placeholder={t('kpi.displayNamePh', 'e.g. % Complete (Weekly)')}
              />
              {state.errors.displayName && (
                <div className={styles.error}>{state.errors.displayName}</div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>{t('kpi.scheduleTemplate', 'Schedule template')}</label>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="schedule_template"
                    checked={state.templateType === 'progress'}
                    onChange={() => dispatch({ type: 'SET_TEMPLATE_TYPE', value: 'progress' })}
                  />
                  {t('kpi.templateProgressOnly', 'Progress Only')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="schedule_template"
                    checked={state.templateType === 'progress_spi'}
                    onChange={() => dispatch({ type: 'SET_TEMPLATE_TYPE', value: 'progress_spi' })}
                  />
                  {t('kpi.templateProgressSpi', 'Progress + SPI')}
                </label>
              </div>
              <div className={styles.help}>
                {t(
                  'kpi.scheduleTemplateHelp',
                  'Progress Only shows the Progress chart. Progress + SPI adds an SPI gauge and history chart for EVM-lite schedule performance.',
                )}
              </div>
            </div>

            {/* Display Mode — preserved from the legacy form. Full renders
                the full chart on the Performance tab; Mini renders a compact
                summary tile. Default Full for the first KPI on a project,
                Mini for subsequent ones. */}
            <div className={styles.formGroup}>
              <label className={styles.label}>{t('kpi.displayMode', 'Display Mode')}</label>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="schedule_display_mode"
                    checked={state.displayMode === 'full'}
                    onChange={() => dispatch({ type: 'SET_DISPLAY_MODE', value: 'full' })}
                  />
                  {t('kpi.displayModeFull', 'Full')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="schedule_display_mode"
                    checked={state.displayMode === 'mini'}
                    onChange={() => dispatch({ type: 'SET_DISPLAY_MODE', value: 'mini' })}
                  />
                  {t('kpi.displayModeMini', 'Mini')}
                </label>
              </div>
              <div className={styles.help}>
                {t(
                  'kpi.displayModeHelp',
                  'Full shows the chart on the Performance tab. Mini shows a compact summary tile.',
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Tolerances */}
        {showStep(2) && (
          <div className={styles.stepContent} data-step="2">
            <h3 className={styles.stepTitle}>{t('kpi.scheduleStep2Title', 'Tolerances')}</h3>
            <p className={styles.stepDesc}>
              {t(
                'kpi.scheduleStep2Desc',
                'Set how far behind baseline the project can drift before turning yellow or red.',
              )}
            </p>

            {/* Progress variance — always shown */}
            <div className={styles.formGroup}>
              <label className={styles.label}>{t('kpi.varianceThresholds', 'Progress variance')}</label>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className={styles.help} style={{ minWidth: 60 }}>
                    {t('kpi.warning', 'Warning')}
                  </span>
                  <input
                    type="number"
                    className={styles.input}
                    style={{ width: 80 }}
                    value={String(state.varianceYellowPP)}
                    onChange={(e) =>
                      dispatch({ type: 'SET_VARIANCE_YELLOW', value: Number(e.target.value) })
                    }
                  />
                  <span className={styles.help}>pp</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className={styles.help} style={{ minWidth: 60 }}>
                    {t('kpi.critical', 'Critical')}
                  </span>
                  <input
                    type="number"
                    className={styles.input}
                    style={{ width: 80 }}
                    value={String(state.varianceRedPP)}
                    onChange={(e) =>
                      dispatch({ type: 'SET_VARIANCE_RED', value: Number(e.target.value) })
                    }
                  />
                  <span className={styles.help}>pp</span>
                </div>
                <span className={styles.help} style={{ opacity: 0.65 }}>
                  {t('kpi.behindBaseline', 'behind baseline')}
                </span>
              </div>
              {state.errors.varianceYellowPP && (
                <div className={styles.error}>{state.errors.varianceYellowPP}</div>
              )}
              {state.errors.varianceRedPP && (
                <div className={styles.error}>{state.errors.varianceRedPP}</div>
              )}
            </div>

            {/* SPI thresholds — only when Progress + SPI is selected */}
            {state.templateType === 'progress_spi' && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.label}>{t('kpi.spiThresholds', 'SPI thresholds')}</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className={styles.help} style={{ minWidth: 60 }}>
                        {t('kpi.warning', 'Warning')}
                      </span>
                      <input
                        type="number"
                        className={styles.input}
                        style={{ width: 80 }}
                        step={0.01}
                        min={0}
                        max={2}
                        value={String(state.spiWarnThreshold)}
                        onChange={(e) =>
                          dispatch({ type: 'SET_SPI_WARN', value: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className={styles.help} style={{ minWidth: 60 }}>
                        {t('kpi.critical', 'Critical')}
                      </span>
                      <input
                        type="number"
                        className={styles.input}
                        style={{ width: 80 }}
                        step={0.01}
                        min={0}
                        max={2}
                        value={String(state.spiCritThreshold)}
                        onChange={(e) =>
                          dispatch({ type: 'SET_SPI_CRIT', value: Number(e.target.value) })
                        }
                      />
                    </div>
                    <span className={styles.help} style={{ opacity: 0.65 }}>
                      {t('kpi.spiRatio', 'ratio (1.0 = on plan)')}
                    </span>
                  </div>
                  {state.errors.spiWarn && (
                    <div className={styles.error}>{state.errors.spiWarn}</div>
                  )}
                  {state.errors.spiCrit && (
                    <div className={styles.error}>{state.errors.spiCrit}</div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    {t('kpi.spiMinBaseline', 'Show SPI after baseline reaches')}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="number"
                      className={styles.input}
                      style={{ width: 80 }}
                      min={0}
                      max={50}
                      value={String(state.spiMinBaselinePct)}
                      onChange={(e) =>
                        dispatch({ type: 'SET_SPI_MIN_BASELINE', value: Number(e.target.value) })
                      }
                    />
                    <span className={styles.help}>%</span>
                  </div>
                  <div className={styles.help}>
                    {t('kpi.spiMinBaselineHelp', 'Suppresses noisy early-project SPI numbers.')}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    {t('kpi.spiWeight', 'SPI weight in combined score')}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className={styles.help} style={{ minWidth: 60 }}>
                        {t('kpi.progress', 'Progress')}
                      </span>
                      <input
                        type="number"
                        className={styles.input}
                        style={{ width: 80 }}
                        min={0}
                        max={100}
                        step={5}
                        value={String(100 - state.spiWeightPct)}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                          dispatch({ type: 'SET_SPI_WEIGHT', value: 100 - v });
                        }}
                      />
                      <span className={styles.help}>%</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className={styles.help} style={{ minWidth: 60 }}>SPI</span>
                      <input
                        type="number"
                        className={styles.input}
                        style={{ width: 80 }}
                        min={0}
                        max={100}
                        step={5}
                        value={String(state.spiWeightPct)}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                          dispatch({ type: 'SET_SPI_WEIGHT', value: v });
                        }}
                      />
                      <span className={styles.help}>%</span>
                    </div>
                    <span className={styles.help} style={{ opacity: 0.65 }}>
                      {t('kpi.weightTotalNote', 'must total 100% (default 50/50)')}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 3: Reporting */}
        {showStep(3) && (
          <div className={styles.stepContent} data-step="3">
            <h3 className={styles.stepTitle}>{t('kpi.scheduleStep3Title', 'Reporting')}</h3>
            <p className={styles.stepDesc}>
              {t(
                'kpi.scheduleStep3Desc',
                'How often this KPI is updated and who is responsible for the update.',
              )}
            </p>

            <div className={styles.formGroup}>
              <label className={styles.label}>{t('kpi.cadence', 'Reporting cadence')}</label>
              <ReportingCadenceWidget
                value={state.cadenceValue}
                onChange={(next) => dispatch({ type: 'SET_CADENCE', value: next })}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                {t('kpi.permissibleDaysLate', 'Permissible days late')}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="number"
                  className={styles.input}
                  style={{ width: 80 }}
                  min={0}
                  value={String(state.permissibleDaysLate)}
                  onChange={(e) =>
                    dispatch({ type: 'SET_PERMISSIBLE_DAYS_LATE', value: Number(e.target.value) })
                  }
                />
                <span className={styles.help}>
                  {t(
                    'kpi.permissibleDaysLateHelp',
                    'days after the cadence due-date before this report is flagged stale',
                  )}
                </span>
              </div>
              {state.errors.permissibleDaysLate && (
                <div className={styles.error}>{state.errors.permissibleDaysLate}</div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>{t('kpi.assignedRole', 'Assigned role')}</label>
              <input
                type="text"
                className={styles.input}
                placeholder={t('kpi.assignedRolePh', 'Role GUID (role picker coming soon)')}
                value={state.assignedRoleGuid || ''}
                onChange={(e) =>
                  dispatch({
                    type: 'SET_ASSIGNED_ROLE',
                    value: e.target.value.trim() || null,
                  })
                }
              />
              <div className={styles.help}>
                {t(
                  'kpi.assignedRoleHelp',
                  'When set, the assigned role-holder is notified when this KPI needs updating. Leave blank to default to the project owner.',
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {showStep(4) && (
          <div className={styles.stepContent} data-step="4">
            <h3 className={styles.stepTitle}>{t('kpi.scheduleStep4Title', 'Review')}</h3>
            <p className={styles.stepDesc}>
              {t(
                'kpi.scheduleStep4Desc',
                'Confirm the configuration, then save. You can change any of these settings later from the same wizard.',
              )}
            </p>

            <ReviewSummary state={state} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerRight}>
          <button className={styles.btn} onClick={onCancel}>
            {t('common.cancel', 'Cancel')}
          </button>
          {state.currentStep > firstStep && !state.showAll && (
            <button className={styles.btn} onClick={handleBack}>
              {t('common.back', 'Back')}
            </button>
          )}
          <button className={styles.btnPrimary} onClick={state.showAll ? handleSave : handleNext}>
            {state.showAll
              ? t('common.save', 'Save')
              : state.currentStep === lastStep
              ? t('common.save', 'Save')
              : t('common.next', 'Next')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Internal: Review summary ──────────────────────────────── */

function ReviewSummary({ state }: { state: WizardState }) {
  const { t } = useAppTranslation('projectConfig');
  const rows: Array<[string, string]> = [
    [t('kpi.displayName', 'Display name'), state.displayName || '—'],
    [
      t('kpi.displayMode', 'Display Mode'),
      state.displayMode === 'mini'
        ? t('kpi.displayModeMini', 'Mini')
        : t('kpi.displayModeFull', 'Full'),
    ],
    [
      t('kpi.scheduleTemplate', 'Schedule template'),
      state.templateType === 'progress_spi'
        ? t('kpi.templateProgressSpi', 'Progress + SPI')
        : t('kpi.templateProgressOnly', 'Progress Only'),
    ],
    [
      t('kpi.varianceThresholds', 'Progress variance'),
      `${state.varianceYellowPP}pp / ${state.varianceRedPP}pp`,
    ],
  ];
  if (state.templateType === 'progress_spi') {
    rows.push([
      t('kpi.spiThresholds', 'SPI thresholds'),
      `${state.spiWarnThreshold} / ${state.spiCritThreshold}`,
    ]);
    rows.push([
      t('kpi.spiMinBaseline', 'Show SPI after baseline reaches'),
      `${state.spiMinBaselinePct}%`,
    ]);
    rows.push([
      t('kpi.spiWeight', 'SPI weight in combined score'),
      `${100 - state.spiWeightPct}% Progress / ${state.spiWeightPct}% SPI`,
    ]);
  }
  rows.push([t('kpi.cadence', 'Reporting cadence'), state.cadenceValue.cadence || '—']);
  rows.push([
    t('kpi.permissibleDaysLate', 'Permissible days late'),
    String(state.permissibleDaysLate),
  ]);
  rows.push([
    t('kpi.assignedRole', 'Assigned role'),
    state.assignedRoleGuid || t('kpi.defaultsToOwner', 'Defaults to project owner'),
  ]);

  return (
    <div>
      {rows.map(([label, value]) => (
        <div key={label} className={styles.reviewRow}>
          <span className={styles.reviewLabel}>{label}</span>
          <span className={styles.reviewValue}>{value}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Helpers ───────────────────────────────────────────────── */

function generateGuid(): string {
  // RFC4122 v4-ish random GUID. Fine for client-generated instance ids;
  // backend regenerates on persist if needed.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default ScheduleKpiWizard;
